import { useState, useEffect, useRef, useMemo } from "react";
import { Zap, Sparkles, TrendingDown } from "lucide-react";
import { useCountUp } from "@/lib/useCountUp";
import { api } from "@/lib/api";

// ---------- Synthetic Price-Intelligence stub (Day-1 launch) ----------
// Purely client-side seed data.  Rotates every ~11s to simulate a live feed.
// Replace with GET /api/v1/price-matches once the Google Shopping Content API
// service-account key is wired.
const PRICE_MATCH_SEED = [
  { sku: "BIC-4CLR-01",     product: "BIC 4-Color Ballpoint",       our: 8.42,   comp: 5.19,   src: "OfficeDepot Wholesale" },
  { sku: "USBC-CBL-2M",     product: "USB-C to USB-C Cable · 2m",   our: 19.99,  comp: 11.47,  src: "Monoprice B2B" },
  { sku: "HP-63-BLK",       product: "HP 63 Black Ink Cartridge",   our: 34.99,  comp: 22.10,  src: "Staples Business" },
  { sku: "3M-N95-08210",    product: "3M N95 Respirator (20 ct)",   our: 42.00,  comp: 28.75,  src: "Grainger" },
  { sku: "BREW-P2-500",     product: "Espresso Beans · 500g",       our: 24.50,  comp: 15.90,  src: "Coffee Direct" },
  { sku: "AA-24PK-DUR",     product: "Duracell AA · 24 pack",       our: 21.99,  comp: 13.44,  src: "CostCo Business" },
  { sku: "TP-108-CRT",      product: "Bath Tissue · 108 ct",        our: 79.99,  comp: 54.20,  src: "Uline" },
  { sku: "DC-4K-WEBCAM",    product: "4K Webcam w/ Auto-Focus",     our: 189.00, comp: 129.99, src: "B&H Photo Pro" },
  { sku: "TEA-EG-100",      product: "Earl Grey · 100 sachets",     our: 32.00,  comp: 19.75,  src: "RestaurantSupply" },
  { sku: "MSK-KN95-50",     product: "KN95 Masks · 50 pack",        our: 44.99,  comp: 22.30,  src: "MedSupply Direct" },
];

function buildMatch(seed, idx) {
  const savings = Math.max(0, seed.our - seed.comp);
  const pct = seed.our > 0 ? (savings / seed.our) * 100 : 0;
  return {
    id: `pm-${idx}-${seed.sku}`,
    sku: seed.sku,
    product: seed.product,
    our_price: seed.our,
    competitor_price: seed.comp,
    savings: Number(savings.toFixed(2)),
    savings_pct: Number(pct.toFixed(1)),
    source: seed.src,
    detected_at: Date.now(),
  };
}

// ---------- Neon Reactor Burn Matrix — breathing geometric grid with cyan pulse ----------
export function NeonReactorBurnMatrix({ stats, receipts }) {
  const [pulseKey, setPulseKey] = useState(0);
  const lastIdsRef = useRef(null);
  // Fire a fresh cyan shockwave the exact tick a NEW receipt ID surfaces in the
  // list. We diff against the previous ID snapshot so identical poll payloads
  // never cause spurious pulses.
  useEffect(() => {
    const ids = new Set((receipts || []).map((r) => r.id));
    const prev = lastIdsRef.current;
    if (prev === null) {
      lastIdsRef.current = ids;
      return;
    }
    let hasNew = false;
    for (const id of ids) if (!prev.has(id)) { hasNew = true; break; }
    if (hasNew) setPulseKey((k) => k + 1);
    lastIdsRef.current = ids;
  }, [receipts]);

  const S = 220;
  const cx = S / 2;
  const cy = S / 2;
  const rings = [30, 50, 70, 90];
  const spokes = Array.from({ length: 12 }, (_, i) => (i * 30 * Math.PI) / 180);
  const burnRatio = (() => {
    const cap = 10000;
    return Math.min(1, Number(stats?.burn_rate || stats?.total_spent || 0) / cap);
  })();
  const outerR = 100;
  const outerCirc = 2 * Math.PI * outerR;
  const filled = burnRatio * outerCirc;

  return (
    <div
      data-testid="neon-reactor-burn-matrix"
      className="glass rounded-2xl p-6 relative overflow-hidden flex flex-col items-center"
      style={{ borderColor: "rgba(16,185,129,0.3)" }}
    >
      <div className="flex items-center justify-between w-full mb-2">
        <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-emerald-400 flex items-center gap-1.5">
          <Zap size={11} /> Neon Reactor · Burn Matrix
        </div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-300/70">
          {Math.round(burnRatio * 100)}%
        </div>
      </div>
      <div className="reactor-breathe relative" style={{ width: S, height: S }}>
        <svg width={S} height={S} viewBox={`0 0 ${S} ${S}`}>
          <defs>
            <radialGradient id="reactor-core" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#10B981" stopOpacity="0.55" />
              <stop offset="45%" stopColor="#10B981" stopOpacity="0.12" />
              <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
            </radialGradient>
            <linearGradient id="reactor-ring-stroke" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#10B981" />
              <stop offset="100%" stopColor="#34D399" />
            </linearGradient>
          </defs>
          {/* Core glow */}
          <circle cx={cx} cy={cy} r="60" fill="url(#reactor-core)" />
          {/* Concentric geometric rings */}
          {rings.map((r, i) => (
            <circle
              key={r}
              cx={cx} cy={cy} r={r}
              fill="none"
              stroke="#10B981"
              strokeWidth={i === rings.length - 1 ? 1.2 : 0.6}
              strokeDasharray={i % 2 ? "3 4" : "1 3"}
              opacity={0.28 + i * 0.08}
            />
          ))}
          {/* Radial spokes forming the grid canvas */}
          {spokes.map((a, i) => (
            <line
              key={i}
              x1={cx + Math.cos(a) * 22}
              y1={cy + Math.sin(a) * 22}
              x2={cx + Math.cos(a) * outerR}
              y2={cy + Math.sin(a) * outerR}
              stroke="#10B981"
              strokeWidth="0.5"
              opacity="0.28"
            />
          ))}
          {/* Burn arc — how hot the reactor runs */}
          <circle
            cx={cx} cy={cy} r={outerR}
            fill="none"
            stroke="url(#reactor-ring-stroke)"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray={`${filled} ${outerCirc - filled}`}
            transform={`rotate(-90 ${cx} ${cy})`}
            style={{ transition: "stroke-dasharray 1100ms cubic-bezier(0.22,1,0.36,1)",
                     filter: "drop-shadow(0 0 6px rgba(16,185,129,0.7))" }}
          />
          {/* Center reading */}
          <text x={cx} y={cy + 4} textAnchor="middle"
                fill="#ecfdf5" fontSize="24" fontWeight="700"
                fontFamily="ui-monospace, monospace"
                style={{ filter: "drop-shadow(0 0 6px rgba(16,185,129,0.65))" }}>
            {(stats?.receipt_count ?? 0)}
          </text>
          <text x={cx} y={cy + 20} textAnchor="middle"
                fill="#6ee7b7" fontSize="8" fontFamily="ui-monospace, monospace"
                style={{ letterSpacing: "0.18em", textTransform: "uppercase" }}>
            live txns
          </text>
        </svg>
        {/* Electric cyan wave pulse — remounts on any state field change */}
        <span
          key={pulseKey}
          data-testid="reactor-cyan-pulse"
          className="reactor-cyan-pulse absolute inset-0 pointer-events-none rounded-full"
          aria-hidden="true"
        />
      </div>

      <PriceIntelligenceTicker />

      <style>{`
        .reactor-breathe {
          border-radius: 9999px;
          animation: reactor-breathe 4.2s ease-in-out infinite;
        }
        @keyframes reactor-breathe {
          0%, 100% {
            filter: drop-shadow(0 0 8px rgba(16,185,129,0.5))
                    drop-shadow(0 0 22px rgba(16,185,129,0.28));
          }
          50% {
            filter: drop-shadow(0 0 14px rgba(16,185,129,0.85))
                    drop-shadow(0 0 40px rgba(16,185,129,0.55));
          }
        }
        .reactor-cyan-pulse {
          box-shadow: 0 0 0 0 rgba(6,182,212,0.75);
          animation: reactor-cyan-shockwave 1200ms cubic-bezier(0.22,1,0.36,1) 1 forwards;
        }
        @keyframes reactor-cyan-shockwave {
          0%   { box-shadow: 0 0 0 0 rgba(6,182,212,0.85), inset 0 0 0 rgba(6,182,212,0); opacity: 1; }
          40%  { box-shadow: 0 0 0 12px rgba(6,182,212,0.35), inset 0 0 30px rgba(6,182,212,0.35); }
          100% { box-shadow: 0 0 0 42px rgba(6,182,212,0),    inset 0 0 0 rgba(6,182,212,0);    opacity: 0; }
        }
      `}</style>
    </div>
  );
}


// ---------- Price Intelligence Ticker · Live-or-Synthetic Harness ----------
// Fetches GET /api/v1/price-matches on mount.  The backend auto-flips:
//   • mode="live"      when GOOGLE_SHOPPING_SA_JSON + GOOGLE_MERCHANT_ID env
//                      secrets are present and the Content API returns rows.
//   • mode="synthetic" otherwise (seed feed identical to the historic Day-1 stub).
// If the network call fails, we degrade gracefully to the local seed rotator
// so the widget never renders empty.
function PriceIntelligenceTicker() {
  const [matches, setMatches] = useState(() => PRICE_MATCH_SEED.slice(0, 4).map(buildMatch));
  const [flareId, setFlareId] = useState(null);
  const [feedMode, setFeedMode] = useState("synthetic"); // "live" | "synthetic"
  const cursorRef = useRef(4);
  const livePoolRef = useRef(null); // filled from /api/v1/price-matches when mode==="live"

  // Initial harness fetch — decide live vs synthetic once per mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/v1/price-matches");
        if (cancelled) return;
        const mode = data?.mode === "live" ? "live" : "synthetic";
        setFeedMode(mode);
        if (mode === "live" && Array.isArray(data?.matches) && data.matches.length > 0) {
          livePoolRef.current = data.matches;
          // Seed the first 4 from the live pool so the reactor lights up immediately.
          setMatches(data.matches.slice(0, 4));
          cursorRef.current = 4;
        }
      } catch {
        // Network/auth failure — keep synthetic seed, silent fallback.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const iv = setInterval(() => {
      let fresh;
      if (feedMode === "live" && livePoolRef.current && livePoolRef.current.length > 0) {
        const pool = livePoolRef.current;
        const src = pool[cursorRef.current % pool.length];
        cursorRef.current += 1;
        // Re-key so React treats each rotation as a fresh flare, even on pool wrap.
        fresh = { ...src, id: `${src.id}-${cursorRef.current}` };
      } else {
        const nextSeed = PRICE_MATCH_SEED[cursorRef.current % PRICE_MATCH_SEED.length];
        cursorRef.current += 1;
        fresh = buildMatch(nextSeed, cursorRef.current);
      }
      setMatches((prev) => [fresh, ...prev].slice(0, 8));
      setFlareId(fresh.id);
      // Fire-and-forget clear — setInterval discards return values so we don't
      // return a cleanup fn from here.  setFlareId(null) is idempotent.
      setTimeout(() => setFlareId(null), 1600);
    }, 11000);
    return () => clearInterval(iv);
  }, [feedMode]);

  const totalSavings = useMemo(
    () => matches.reduce((acc, m) => acc + (m.savings || 0), 0),
    [matches],
  );
  const rolled = useCountUp(totalSavings, 1400);
  // Demo-only platform-fee visual (1.5% of savings) — NOT wired to any real
  // fee capture.  Stripe handles all live currency + interchange under its
  // own license when international customers check out.
  const platformFeeDemo = totalSavings * 0.015;
  const rolledFee = useCountUp(platformFeeDemo, 1400);

  return (
    <div
      data-testid="price-intelligence-ticker"
      className="mt-4 w-full rounded-xl relative overflow-hidden"
      style={{
        background: "linear-gradient(180deg, rgba(2,6,23,0.7), rgba(6,182,212,0.06))",
        border: "1px solid rgba(6,182,212,0.28)",
      }}
    >
      {/* Header row: rolling savings odometer + demo fee chip */}
      <div className="flex flex-wrap items-end justify-between gap-3 px-4 pt-4 pb-3 border-b border-cyan-500/15">
        <div>
          <div className="text-[9px] font-mono uppercase tracking-[0.28em] text-cyan-300 flex items-center gap-1.5">
            <Sparkles size={10} /> Price Intelligence · Auto-Sweep
          </div>
          <div
            data-testid="price-savings-odometer"
            className="text-white text-3xl font-mono font-bold tracking-tight mt-1"
            style={{
              background: "linear-gradient(90deg, #67e8f9, #fde047)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              filter: "drop-shadow(0 0 6px rgba(103,232,249,0.45))",
            }}
          >
            ${Number(rolled).toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-1">
            Total Automated Supply-Chain Savings
          </div>
        </div>
        <div
          data-testid="price-platform-fee-chip"
          className="rounded-lg px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-cyan-200 flex items-center gap-1.5"
          style={{
            background: "rgba(6,182,212,0.1)",
            border: "1px solid rgba(6,182,212,0.35)",
          }}
          title="Demo visual only — not a live fee capture. Stripe handles real FX + interchange."
        >
          <TrendingDown size={10} /> 1.5% platform · ${Number(rolledFee).toFixed(2)}
        </div>
      </div>

      {/* Horizontal match ticker */}
      <div
        data-testid="price-match-strip"
        className="flex gap-2 overflow-x-auto no-scrollbar px-3 py-3"
        style={{ scrollbarWidth: "none" }}
      >
        {matches.map((m) => {
          const isFresh = m.id === flareId;
          return (
            <div
              key={m.id}
              data-testid={`price-match-${m.sku}`}
              data-fresh={isFresh ? "true" : "false"}
              className={`shrink-0 min-w-[220px] rounded-lg px-3 py-2 relative overflow-hidden ${isFresh ? "price-match-flare" : ""}`}
              style={{
                background: "rgba(15,23,42,0.75)",
                border: "1px solid rgba(6,182,212,0.28)",
              }}
            >
              <div className="text-[9px] font-mono uppercase tracking-widest text-cyan-300 truncate">{m.sku}</div>
              <div className="text-white text-xs font-semibold truncate">{m.product}</div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-slate-500 text-[11px] font-mono line-through">${m.our_price.toFixed(2)}</span>
                <span className="text-emerald-300 text-sm font-mono font-bold">${m.competitor_price.toFixed(2)}</span>
                <span className="ml-auto text-yellow-300 text-[10px] font-mono">-{m.savings_pct}%</span>
              </div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500 truncate mt-0.5">
                {m.source}
              </div>
              {/* Neon-cyan + gold laser sweep — only on the newest match */}
              {isFresh && (
                <span
                  aria-hidden="true"
                  data-testid="price-match-flare"
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(115deg, transparent 0%, transparent 35%, rgba(103,232,249,0.55) 48%, rgba(253,224,71,0.55) 55%, transparent 68%, transparent 100%)",
                    animation: "price-flare-sweep 1500ms cubic-bezier(0.22,1,0.36,1) 1",
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      <div
        data-testid="price-feed-mode-footer"
        data-mode={feedMode}
        className="px-4 pb-3 text-[9px] font-mono uppercase tracking-widest text-slate-500"
      >
        {feedMode === "live"
          ? "Live · Google Shopping Content API v2.1"
          : "Stubbed feed · switches to live Google Shopping Content API on key delivery"}
      </div>

      <style>{`
        @keyframes price-flare-sweep {
          0%   { transform: translateX(-120%); opacity: 0; }
          20%  { opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translateX(120%); opacity: 0; }
        }
        .price-match-flare {
          box-shadow: 0 0 0 1px rgba(103,232,249,0.55), 0 12px 30px -10px rgba(103,232,249,0.35);
          animation: price-match-highlight 1500ms cubic-bezier(0.22,1,0.36,1);
        }
        @keyframes price-match-highlight {
          0%   { box-shadow: 0 0 0 1px rgba(103,232,249,0.85), 0 0 22px rgba(253,224,71,0.45); }
          100% { box-shadow: 0 0 0 1px rgba(103,232,249,0.28), 0 0 0 rgba(253,224,71,0); }
        }
        [data-testid=price-match-strip]::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
}


